/**
 * Shared PDF rendering primitives.
 *
 * Layout constants, color/format helpers, and the standard header/footer
 * blocks that every connector-specific renderer reuses.
 *
 * Renderers in this package (render-ga4, render-gsc, ...) import from here
 * to keep the look consistent across reports.
 */

import { rgb, type PDFFont, type PDFPage, type RGB } from "pdf-lib";
import type { AgencyBrandConfig } from "@agencylens/shared";

/* ------------------------------------------------------- layout constants */

export const MARGIN_X = 48;
export const MARGIN_Y = 48;
/** US Letter at 72dpi */
export const PAGE_W = 612;
export const PAGE_H = 792;
export const BODY = PAGE_H - 2 * MARGIN_Y;

export interface PDFFonts {
  regular: PDFFont;
  bold: PDFFont;
}

/* ----------------------------------------------------------- color helper */

export function hexToRgb(hex: string): RGB {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return rgb(0.12, 0.25, 0.69); // fallback Tailwind blue-700
  const num = parseInt(m[1]!, 16);
  return rgb(
    ((num >> 16) & 0xff) / 255,
    ((num >> 8) & 0xff) / 255,
    (num & 0xff) / 255,
  );
}

/* ---------------------------------------------------------- format helpers */

export function fmtNumber(n: number): string {
  if (!Number.isFinite(n)) return "—";
  if (Math.abs(n) >= 1000)
    return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
  return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

export function fmtPercent(fraction: number): string {
  if (!Number.isFinite(fraction)) return "—";
  return `${(fraction * 100).toFixed(1)}%`;
}

export function fmtPosition(n: number): string {
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(1);
}

export function fmtDateRange(start: string, end: string): string {
  return `${start} to ${end}`;
}

/* ----------------------------------------------------- header + footer */

export function drawHeader(
  page: PDFPage,
  fonts: PDFFonts,
  brand: AgencyBrandConfig,
  subtitle = "Performance Report",
): void {
  const primary = hexToRgb(brand.primaryColor);

  page.drawRectangle({
    x: 0,
    y: PAGE_H - 24,
    width: PAGE_W,
    height: 24,
    color: primary,
  });

  page.drawText(brand.agencyName, {
    x: MARGIN_X,
    y: PAGE_H - 60,
    size: 20,
    font: fonts.bold,
    color: rgb(0.1, 0.1, 0.1),
  });

  page.drawText(subtitle, {
    x: MARGIN_X,
    y: PAGE_H - 80,
    size: 11,
    font: fonts.regular,
    color: rgb(0.4, 0.4, 0.4),
  });
}

export function drawFooter(
  page: PDFPage,
  fonts: PDFFonts,
  brand: AgencyBrandConfig,
  pageNum: number,
  pageCount: number,
): void {
  const text = brand.footerText ?? "Generated with AgencyLens";
  page.drawText(text, {
    x: MARGIN_X,
    y: MARGIN_Y / 2,
    size: 9,
    font: fonts.regular,
    color: rgb(0.5, 0.5, 0.5),
  });
  const right = `Page ${pageNum} of ${pageCount}`;
  const w = fonts.regular.widthOfTextAtSize(right, 9);
  page.drawText(right, {
    x: PAGE_W - MARGIN_X - w,
    y: MARGIN_Y / 2,
    size: 9,
    font: fonts.regular,
    color: rgb(0.5, 0.5, 0.5),
  });
}

/* ------------------------------------------------------- KPI grid */

export interface KpiCard {
  label: string;
  value: string;
}

/**
 * Draw a 2-column grid of KPI cards. Returns the y-coordinate just below the
 * last card so the caller can continue layout from there.
 */
export function drawKpiGrid(
  page: PDFPage,
  fonts: PDFFonts,
  cards: KpiCard[],
  topY: number,
): number {
  const cardW = (PAGE_W - 2 * MARGIN_X - 16) / 2;
  const cardH = 72;
  const gap = 12;

  for (let i = 0; i < cards.length; i++) {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x = MARGIN_X + col * (cardW + 16);
    const cardY = topY - row * (cardH + gap);

    page.drawRectangle({
      x,
      y: cardY - cardH,
      width: cardW,
      height: cardH,
      color: rgb(0.97, 0.97, 0.98),
      borderColor: rgb(0.88, 0.88, 0.92),
      borderWidth: 1,
    });

    page.drawText(cards[i]!.label, {
      x: x + 14,
      y: cardY - 22,
      size: 10,
      font: fonts.regular,
      color: rgb(0.4, 0.4, 0.4),
    });
    page.drawText(cards[i]!.value, {
      x: x + 14,
      y: cardY - 50,
      size: 22,
      font: fonts.bold,
      color: rgb(0.1, 0.1, 0.1),
    });
  }

  const rows = Math.ceil(cards.length / 2);
  return topY - rows * (cardH + gap);
}

/* ---------------------------------------------- table primitives */

export interface TableColumn<T> {
  label: string;
  width: number;
  align?: "right";
  /** Render the cell text from the row */
  format: (row: T) => string;
}

export function drawTable<T>(
  page: PDFPage,
  fonts: PDFFonts,
  brand: AgencyBrandConfig,
  cols: TableColumn<T>[],
  rows: T[],
  topY: number,
): void {
  let y = topY;

  // Header band
  let x = MARGIN_X;
  page.drawRectangle({
    x: MARGIN_X - 4,
    y: y - 6,
    width: PAGE_W - 2 * MARGIN_X + 8,
    height: 22,
    color: hexToRgb(brand.primaryColor),
    opacity: 0.08,
  });
  for (const col of cols) {
    const text = col.label;
    const w = fonts.bold.widthOfTextAtSize(text, 9);
    const tx = col.align === "right" ? x + col.width - w - 6 : x + 4;
    page.drawText(text, {
      x: tx,
      y,
      size: 9,
      font: fonts.bold,
      color: rgb(0.2, 0.2, 0.2),
    });
    x += col.width;
  }
  y -= 22;

  // Body rows
  for (let i = 0; i < rows.length; i++) {
    if (i % 2 === 0) {
      page.drawRectangle({
        x: MARGIN_X - 4,
        y: y - 4,
        width: PAGE_W - 2 * MARGIN_X + 8,
        height: 18,
        color: rgb(0.98, 0.98, 0.99),
      });
    }
    x = MARGIN_X;
    for (const col of cols) {
      const text = col.format(rows[i]!);
      const w = fonts.regular.widthOfTextAtSize(text, 9);
      const tx = col.align === "right" ? x + col.width - w - 6 : x + 4;
      page.drawText(text, {
        x: tx,
        y,
        size: 9,
        font: fonts.regular,
        color: rgb(0.18, 0.18, 0.18),
      });
      x += col.width;
    }
    y -= 18;
  }
}

/* -------------------------------------------------- fallback line chart */

export interface FallbackChartOptions {
  topY: number;
  /** Chart height in points; default 280 */
  height?: number;
  /** Y-axis label formatter (raw → display); default `fmtNumber` */
  labelFormat?: (n: number) => string;
  /** True means "lower y-value = better" — inverts the data display for things
   *  like search position rank. */
  invertY?: boolean;
}

export function drawFallbackLineChart(
  page: PDFPage,
  fonts: PDFFonts,
  brand: AgencyBrandConfig,
  points: Array<{ date: string; value: number }>,
  opts: FallbackChartOptions,
): void {
  const primary = hexToRgb(brand.primaryColor);
  const chartX = MARGIN_X;
  const chartY = opts.topY;
  const chartW = PAGE_W - 2 * MARGIN_X;
  const chartH = opts.height ?? 280;
  const fmt = opts.labelFormat ?? fmtNumber;

  page.drawRectangle({
    x: chartX,
    y: chartY - chartH,
    width: chartW,
    height: chartH,
    color: rgb(0.98, 0.98, 0.99),
    borderColor: rgb(0.85, 0.85, 0.9),
    borderWidth: 0.5,
  });

  if (points.length === 0) {
    page.drawText("No data for the selected range.", {
      x: chartX + 16,
      y: chartY - chartH / 2,
      size: 11,
      font: fonts.regular,
      color: rgb(0.5, 0.5, 0.5),
    });
    return;
  }

  const values = points.map((p) => p.value);
  const maxV = Math.max(...values, 1);
  const minV = opts.invertY ? Math.min(...values, 1) : 0;
  const range = Math.max(maxV - minV, 1);
  const npts = points.length;
  const stepX = npts > 1 ? (chartW - 40) / (npts - 1) : 0;

  // Gridlines (5)
  for (let i = 0; i <= 4; i++) {
    const y = chartY - 20 - ((chartH - 40) * i) / 4;
    page.drawLine({
      start: { x: chartX + 20, y },
      end: { x: chartX + chartW - 20, y },
      thickness: 0.4,
      color: rgb(0.9, 0.9, 0.94),
    });
    const labelVal = opts.invertY
      ? minV + (range * i) / 4
      : maxV - (range * i) / 4;
    page.drawText(fmt(labelVal), {
      x: chartX + 2,
      y: y - 3,
      size: 7,
      font: fonts.regular,
      color: rgb(0.55, 0.55, 0.55),
    });
  }

  const valueToY = (v: number): number => {
    const t = opts.invertY ? (v - minV) / range : (maxV - v) / range;
    return chartY - 20 - (chartH - 40) * (1 - t);
  };

  for (let i = 1; i < npts; i++) {
    const x0 = chartX + 20 + (i - 1) * stepX;
    const x1 = chartX + 20 + i * stepX;
    const y0 = valueToY(points[i - 1]!.value);
    const y1 = valueToY(points[i]!.value);
    page.drawLine({
      start: { x: x0, y: y0 },
      end: { x: x1, y: y1 },
      thickness: 1.5,
      color: primary,
    });
  }

  // x-axis labels (first, middle, last)
  const labelIndices = [0, Math.floor((npts - 1) / 2), npts - 1].filter(
    (v, i, a) => a.indexOf(v) === i,
  );
  for (const idx of labelIndices) {
    const x = chartX + 20 + idx * stepX;
    const label = points[idx]!.date;
    const w = fonts.regular.widthOfTextAtSize(label, 7);
    page.drawText(label, {
      x: x - w / 2,
      y: chartY - chartH + 4,
      size: 7,
      font: fonts.regular,
      color: rgb(0.5, 0.5, 0.5),
    });
  }
}
